import type { TOC } from '@ember/component/template-only';
import Heading from './heading';
import type { HeadingLevel } from './heading';
import Text from './text';
import type { ComponentLike } from '@glint/template';

// ---------------------------------------------------------------------------
// SectionPanel – card container with icon header
// ---------------------------------------------------------------------------

/**
 * Color theme for the icon.
 *
 * All 15 names are preserved for API back-compat with the 20+ tio-admin
 * + tio-employee consumers, but they collapse onto **6 TIO canonical
 * visual variants** via the `.app-section-panel--<color>` CSS modifier —
 * see `addons/tio-ui/src/styles/`. Same mapping as IconBadge:
 *
 *   gray                                        → neutral
 *   emerald, green                              → success
 *   teal, cyan, sky, blue, indigo               → info (teal)
 *   violet, purple                              → brand (purple)
 *   pink, rose, red                             → danger
 *   amber, orange                               → warning
 */
export type SectionPanelIconColor =
  | 'gray'
  | 'emerald'
  | 'green'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'pink'
  | 'rose'
  | 'red'
  | 'amber'
  | 'orange';

interface SectionPanelSignature {
  Args: {
    /** Phosphor icon component to display */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text for the header */
    title: string;
    /** Color for the icon */
    iconColor?: SectionPanelIconColor;
    /** Icon size (default: 20) */
    iconSize?: number;
    /**
     * Render the title as a semantic heading of this level so the panel
     * participates in the page's heading outline. Visuals are unchanged
     * (base size, semibold). Default: a non-heading span.
     */
    headingLevel?: HeadingLevel;
    /**
     * Deprecated and ignored. It documented a default of `true` while the
     * template applied the 12px step unless it was passed, so 100 of 106 call
     * sites rendered off the standard panel radius. A panel now always takes
     * the standard surface; for a panel nested inside another panel, pass
     * `class="app-section-panel--rounded-lg"`.
     */
    roundedXl?: boolean;
  };
  Blocks: {
    /** Optional content on the right side of the header (e.g., badge, button) */
    trailing?: [];
    /** Main body content */
    body: [];
  };
  Element: HTMLDivElement;
}

/**
 * SectionPanel - A card container with icon header
 *
 * Displays a rounded card with icon + title header and body content.
 * Commonly used throughout dashboards for consistent panel layouts.
 *
 * Built on the `.app-section-panel*` semantic class family — the color
 * modifier on the wrapping div sets `--section-icon-color` from the
 * canonical TIO recipes, and the header icon composes against it via
 * `color: var(--section-icon-color)`.
 *
 * @example Basic usage
 * ```gts
 * import SectionPanel from 'tio-ui/components/section-panel';
 *
 * <SectionPanel @icon={{PhUsers}} @title="Team Members" @iconColor="violet">
 *   <:body>
 *     <p>Panel content here</p>
 *   </:body>
 * </SectionPanel>
 * ```
 *
 * @example With trailing badge
 * ```gts
 * <SectionPanel @icon={{PhQueue}} @title="Pending Approvals" @iconColor="violet">
 *   <:trailing>
 *     <Chip @intent="warning" @size="sm">5</Chip>
 *   </:trailing>
 *   <:body>
 *     <p>Approval list here</p>
 *   </:body>
 * </SectionPanel>
 * ```
 */
const SectionPanel: TOC<SectionPanelSignature> = <template>
  <div
    data-test-section-panel
    class="app-section-panel app-section-panel--{{if @iconColor @iconColor 'gray'}}"
    ...attributes
  >
    <div data-test-section-panel-header class="app-section-panel-header">
      <div class="app-section-panel-header-content">
        {{#let @icon as |Icon|}}
          <Icon @size={{if @iconSize @iconSize 20}} class="app-section-panel-icon" />
        {{/let}}
        {{#if @headingLevel}}
          <Heading @level={{@headingLevel}} @size="base">{{@title}}</Heading>
        {{else}}
          <Text @weight="semibold">{{@title}}</Text>
        {{/if}}
      </div>
      {{#if (has-block "trailing")}}
        <div data-test-section-panel-trailing class="app-section-panel-trailing">
          {{yield to="trailing"}}
        </div>
      {{/if}}
    </div>
    <div data-test-section-panel-body class="app-section-panel-body">
      {{yield to="body"}}
    </div>
  </div>
</template>;

export default SectionPanel;

export type { SectionPanelSignature };
